import React, { useEffect, useState } from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";

const createForm = () => ({
	loaded: false,
	saving: false,
	depositAmountRequiredRub: "",
	depositDaysLimit: "",
	error: "",
});

const minorToRub = (value) => {
	const amount = Number(value || 0) / 100;
	return Number.isInteger(amount) ? String(amount) : String(amount.toFixed(2));
};

const normalizePositiveDecimalInput = (value) => {
	const normalized = String(value || "").replace(",", ".").replace(/[^\d.]/g, "");
	const [integerPart, ...decimalParts] = normalized.split(".");
	return decimalParts.length ? `${integerPart}.${decimalParts.join("")}` : integerPart;
};

const normalizePositiveIntegerInput = (value) => String(value || "").replace(/[^\d]/g, "");

export default function EcorAirdropSettingsModal({ Interface, visible, onDismiss }) {
	const [form, setForm] = useState(createForm);
	const patch = (values) => setForm((current) => ({ ...current, ...values }));

	useEffect(() => {
		if (!visible) return undefined;

		let active = true;
		setForm(createForm());
		Interface.getEcorAirdropSettings()
			.then((settings) => {
				if (!active) return;
				patch({
					loaded: true,
					depositAmountRequiredRub: minorToRub(settings.depositAmountRequired),
					depositDaysLimit: String(settings.depositDaysLimit),
				});
			})
			.catch((error) => {
				if (!active) return;
				patch({
					loaded: true,
					error: error?.text || error?.message || String(error),
				});
			});

		return () => {
			active = false;
		};
	}, [Interface, visible]);

	const save = () => {
		const amountRub = Number(String(form.depositAmountRequiredRub).replace(",", "."));
		const amountMinor = Math.round(amountRub * 100);
		const days = Number(form.depositDaysLimit);

		if (!Number.isFinite(amountRub) || amountRub <= 0 || !Number.isSafeInteger(amountMinor) || amountMinor < 1 || Math.abs(amountRub * 100 - amountMinor) > 0.000001) {
			patch({ error: "Minimum deposit must be greater than zero and contain no more than two decimal places." });
			return;
		}

		if (!Number.isSafeInteger(days) || days < 1) {
			patch({ error: "Deposit validity period must be a whole number of at least 1 day." });
			return;
		}

		patch({ saving: true, error: "" });
		Interface.saveEcorAirdropSettings({
			depositAmountRequired: amountMinor,
			depositDaysLimit: days,
		})
			.then(() => {
				Interface.addNoty("Success", "ECOR Air-drop settings were updated");
				onDismiss();
			})
			.catch((error) => {
				patch({
					saving: false,
					error: error?.text || error?.message || String(error),
				});
			});
	};

	return (
		<Modal
			visible={visible}
			header="ECOR Air-drop settings"
			onDismiss={onDismiss}
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="s">
						<Button onClick={onDismiss} disabled={form.saving}>
							Cancel
						</Button>
						<Button variant="primary" onClick={save} disabled={!form.loaded} loading={form.saving}>
							Save
						</Button>
					</SpaceBetween>
				</Box>
			}>
			{form.loaded ? (
				<SpaceBetween size="m">
					{form.error ? <Alert type="error">{form.error}</Alert> : null}
					<FormField label="Minimum successful deposit (RUB)" description="A single successful deposit must be at least this amount.">
						<Input
							type="number"
							inputMode="decimal"
							min="0.01"
							step="0.01"
							value={form.depositAmountRequiredRub}
							disabled={form.saving}
							onChange={({ detail }) => patch({ depositAmountRequiredRub: normalizePositiveDecimalInput(detail.value), error: "" })}
						/>
					</FormField>
					<FormField label="Deposit validity period (days)" description="The deposit is checked relative to the time of each Air-drop claim attempt.">
						<Input
							type="number"
							inputMode="numeric"
							min="1"
							step="1"
							value={form.depositDaysLimit}
							disabled={form.saving}
							onChange={({ detail }) => patch({ depositDaysLimit: normalizePositiveIntegerInput(detail.value), error: "" })}
						/>
					</FormField>
				</SpaceBetween>
			) : (
				<Box padding={{ top: "xxxl", bottom: "xxxl" }} textAlign="center">
					<Spinner size="large" />
				</Box>
			)}
		</Modal>
	);
}
